import AddIcon from '@mui/icons-material/Add';
import DeleteIconButton from '../../components/DeleteIconButton';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import RestoreIcon from '@mui/icons-material/Restore';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import { Box, Button, Divider, FormControlLabel, Grid, IconButton, Switch, Tooltip, Typography } from '@mui/material';
import React from 'react';

import { toLicenseeId } from '../../components/FormLicenseeContext';
import RemoteAutocomplete from '../../components/RemoteAutocomplete';
import { ContextStore } from '../../store/contextStore';
import { useIsWidthUp } from '../../utils';
import { granterGrant, isMcpEntitlement, NO, YES } from './mcpEntitlements';
import { mcpPermissionName } from './mcpPermissions';
import { actionName, capitalize, isMcpPermission, MCP_GROUP_TITLE, orderActions, SCOPE } from './utils';
import useConfirmDialog from '../../components/useConfirmDialog';

const ALL_TAGS_RESTRICTED_HINT = 'Only franchise admins can grant access to all tags';

/**
 * Owner Q9 (`./mcpEntitlements.js`): the granter's own grant of a client-MCP permission on the operator this group is
 * for -- `{held, allTags, tags}` --, or null for any other permission (nothing to check).
 */
const useGranterGrant = (ag) => {
    const { user, isFranchise } = React.useContext(ContextStore);
    const licenseeId = toLicenseeId(ag?.licensee) ?? null;
    return (scope, action) => (isMcpEntitlement(scope, action)
        ? granterGrant(user?.permissions, scope, action, { franchiseParent: !!isFranchise, licenseeId })
        : null);
};

const explicitHint = (scope, action) => `Only someone who holds “${mcpPermissionName(scope, action)}” explicitly (not through Full access) can grant it`;
const allTagsHint = (scope, action) => `Only someone who holds “${mcpPermissionName(scope, action)}” explicitly on all tags can grant it on all tags`;

/** Before the first AI-over-MCP action of a list (`orderActions` puts them last): their heading. */
const startsMcpGroup = (scope, actions, idx) => isMcpPermission(scope, actions[idx])
    && (idx === 0 || !isMcpPermission(scope, actions[idx - 1]));

const McpGroupHeading = ({ isSmallScreen }) => (
    <Grid item xs={12} pl={isSmallScreen ? 1 : 2} pt={1}>
        <Typography variant="overline" color="text.secondary">{MCP_GROUP_TITLE}</Typography>
    </Grid>
);

const PermissionScope = (props) => {
    const [confirmDelete, confirmDeleteDialog] = useConfirmDialog();
    const { hasPerm, canGrantAllTags, grantableAtagIds } = React.useContext(ContextStore);
    // readOnly: the group is template-managed (TourOptima staff only) — same lockdown
    // as lacking the agroup.write permission.
    const hasAccessGroupEditPerm = hasPerm('agroup', 'write') && !props.readOnly
    const allTagsRestricted = !canGrantAllTags();
    const grantableIds = grantableAtagIds();
    const onRemovePermission = async (ev, action) => {
        if (await confirmDelete({ body: `Are you sure you want to remove the "${props.scope}.${action}" permission?`, okButtonText: 'Remove', destructive: true })) {
            props.onRemovePermission(props.scope, action);
        }
    };

    const onAllTagsSwitch = (ev) => {
        const action = ev.target.name;
        props.onAllTagsSwitch(props.existingPerms[action]);
    };

    // Turning "All" on: refused to someone who does not hold this MCP permission on all tags (Q9; the backend decides
    // when the session cannot tell).
    const allTagsBlocked = (action) => props.grantOf(props.scope, action)?.allTags === NO;
    const allTagsTooltip = (action) => {
        if (props.existingPerms[action].all_tags) return '';
        if (allTagsRestricted) return ALL_TAGS_RESTRICTED_HINT;
        return allTagsBlocked(action) ? allTagsHint(props.scope, action) : '';
    };
    // The tags offered: the franchise's grantable ones, and for an MCP permission the ones the granter holds it on.
    const tagOptions = (action) => (data) => {
        const mcpTags = props.grantOf(props.scope, action)?.tags;
        return data.filter((t) => (!grantableIds || grantableIds.includes(t.id)) && (!Array.isArray(mcpTags) || mcpTags.includes(t.id)));
    };

    const getHelperText = (scope, action, ag) => {
        if (scope === "pre_trip_item" && action === "visibility") {
            return `Members of this group (${ag.name}) will be able to see pre-trip items assigned to one of the groups listed here.`
        } else if (scope === "chat" && action === "tour duration-only access") {
            return <>Members of this group ({ag.name}) will only have access to chat conversations during the tour duration.<span style={{ display: 'block', marginTop: '0.5em', fontStyle: 'italic' }}>Note: if this permission is set, "read" access on the Chat scope should generally not be granted as well, unless you specifically intend to provide full read access outside of the tour duration.</span></>
        } else if (scope === "chat" && action === "read") {
            return <>Members of this group ({ag.name}) will be able to read chat conversations of members in the groups listed here. Group marked with [*] is the group to which all guests belong.<span style={{ display: 'block', marginTop: '0.5em', fontStyle: 'italic' }}>Note: leave the access groups list empty to restrict read access to only the tours that the user is a participant of.</span></>
        } else {
            return `Members of this group (${ag.name}) will be able to perform "${action}" operation on the members of groups listed here. Group marked with [*] is the group to which all guests belong.`
        }
    };

    console.log('permissionScope', props);
    const actions = orderActions(props.scope, Object.keys(props.existingPerms));
    return (
        <>
            {confirmDeleteDialog}
            {actions
                .map((action, idx) => (
                    <React.Fragment key={action}>
                    {startsMcpGroup(props.scope, actions, idx) && <McpGroupHeading isSmallScreen={props.isSmallScreen} />}
                    <Grid item xs={12} container alignItems="center" p={props.isSmallScreen ? 1 : 0} >
                        <Grid item xs={6} sm={6} container alignItems="center" alignContent="center" justifyContent={'flex-start'} pl={props.isSmallScreen ? 1 : 2} >
                            <Typography fontWeight={500}>{actionName(props.scope, action) || capitalize(action)}</Typography>
                        </Grid>
                        {(!props.existingPerms[action].has_target_agroups && !props.existingPerms[action].has_target_tags) &&  
                            <Grid item xs={6} container  justifyContent={'flex-end'} pr={0.5}>
                                <DeleteIconButton title="Remove permission" size="medium" fontSize="medium" onClick={(ev) => onRemovePermission(ev, action)} allowed={hasAccessGroupEditPerm} />
                            </Grid>}
                        <Grid item xs={6} sm={6} container alignItems="center" alignContent="center" justifyContent={'flex-end'}>
                            { props.existingPerms[action].has_target_tags && <>
                                {/* Turning ON is the restricted action (franchise admins; an MCP permission's holders on all tags); turning OFF stays allowed */}
                                <Tooltip title={allTagsTooltip(action)}>
                                    <span>
                                        <FormControlLabel control={<Switch checked={props.existingPerms[action].all_tags} onChange={onAllTagsSwitch} name={action} color="primary" disabled={!hasAccessGroupEditPerm || (!props.existingPerms[action].all_tags && (allTagsRestricted || allTagsBlocked(action)))} />} label="All" />
                                    </span>
                                </Tooltip>
                            </> }
                        </Grid>
                        <Grid item xs={12} container  alignItems={'flex-start'} pt={1}  justifyContent={!props.isSmallScreen ? 'center' : 'flex-end'}>
                        <Grid item xs={10} sm={10} pl={props.isSmallScreen ? 0 : 2}>
                            { props.existingPerms[action].has_target_agroups ?
                                <>
                                    <RemoteAutocomplete
                                        multiple
                                        resource="access_group"
                                        label="Access groups"
                                        urlSuffix={null}
                                        value={props.existingPerms[action].target_agroups}
                                        size="small"
                                        variant="filled"
                                        filterSelected
                                        getOptionLabel={(option) => {
                                            if (option.is_guest_group) {
                                                return `${option && option.name ? option.name : ''} [*]`;
                                            } else {
                                                return option && option.name ? option.name : '';
                                            }
                                        }}
                                        onChange={(ev, tagroups, reason) => {
                                            console.log('node target agroups change', action, tagroups, props.existingPerms[action]);
                                            props.onTargetAccessGroupsChange(props.existingPerms[action], tagroups);
                                        }}
                                        textFieldProps={{
                                            helperText: getHelperText(props.scope, action, props.ag),
                                        }}
                                        
                                    />
                                 </> : <>
                                { props.existingPerms[action].has_target_tags && 
                                <>
                                    <RemoteAutocomplete
                                        multiple
                                                disabled={props.existingPerms[action].all_tags || !hasAccessGroupEditPerm}
                                        resource="access_tag"
                                        label="Access tags"
                                        urlSuffix={null}
                                        mapResults={tagOptions(action)}
                                        value={props.existingPerms[action].atags}
                                        size="small"
                                        variant="filled"
                                        filterSelected
                                        onChange={(ev, atags, reason) => {
                                            console.log('node atag change', action, atags, props.existingPerms[action]);
                                            props.onAccessTagsChange(props.existingPerms[action], atags);
                                        }}
                                        textFieldProps={{
                                            helperText: props.scope === "chat" && action === "tour duration-only access"
                                                ? <>Members of this group ({props.ag.name}) will only have access to chat conversations tagged with these tags during the tour duration.<span style={{ display: 'block', fontStyle: 'italic' }}>Note: if this permission is set, "read" access on the Chat scope should generally not be granted as well, unless you specifically intend to provide full read access outside of the tour duration.</span></>
                                                : props.scope === "chat" && action === "read"
                                                    ? <>Members of this group ({props.ag.name}) will be able to read chat conversations tagged with these tags.<span style={{ display: 'block', fontStyle: 'italic' }}>Note: leave access tags blank and "All" unchecked to restrict read access to only the tours that the user is a participant of.</span></>
                                                    : `Members of this group (${props.ag.name}) will be able to perform "${actionName(props.scope, action) || action}" operation on records of type "${props.scope}" tagged with these tags`
                                        }}
                                    />
                                    </>}
                            </> }
                            </Grid>
                            {(props.existingPerms[action].has_target_agroups || props.existingPerms[action].has_target_tags) &&  
                            <Grid item xs={2} sm={2} container  justifyContent={'flex-end'} pr={0.5}>
                                <DeleteIconButton title="Remove permission" size="medium" fontSize="medium" onClick={(ev) => onRemovePermission(ev, action)} allowed={hasAccessGroupEditPerm} />
                            </Grid>}
                        </Grid>
                        <Grid item xs={12}  p={props.isSmallScreen ? 0 : 2} pt={props.isSmallScreen ? 1 : 2} pb={0} >   
                            <Divider color="#D8D8D8"/>
                        </Grid>
                    </Grid>
                    </React.Fragment>
                ))}
        </>
    );
};

const AccessGroupEditPermissions = (props) => {
    const existingPerms = {};
    const potentialPerms = {};
    const exKeys = {};
    const isSmallScreen = !useIsWidthUp('sm');
    const [open, setOpen] = React.useState([]);
    const { hasPerm, canGrantAllTags } = React.useContext(ContextStore);
    const hasAccessGroupEditPerm = hasPerm('agroup', 'write') && !props.readOnly;
    const allTagsRestricted = !canGrantAllTags();
    const grantOf = useGranterGrant(props.data);
    // Q9: may the bulk actions send it? Any other permission, yes; an MCP one only when the granter certainly holds it
    // (on all tags, for "All"). What they leave out is said next to the batch's result (`leftOut`).
    const mayGrant = (scope, action) => (grantOf(scope, action)?.held ?? YES) === YES;
    const mayGrantAllTags = (scope, action) => (grantOf(scope, action)?.allTags ?? YES) === YES;

    // --- Batch helpers ---
    const sendBatch = (actions, leftOut) => {
        if (actions.length > 0 || leftOut.length > 0) props.onBatchPermissions(actions, { leftOut });
    };

    const grantAllPerms = (scopeFilter) => {
        const actions = [];
        const leftOut = [];
        Object.keys(props.permissionMap).filter((s) => !SCOPE(s)?.legacy).forEach((scope) => {
            if (scopeFilter && scope !== scopeFilter) return;
            Object.keys(props.permissionMap[scope]).forEach((action) => {
                if (!existingPerms[scope]?.[action]) {
                    if (mayGrant(scope, action)) actions.push({ op: 'add', scope, action });
                    else leftOut.push({ op: 'add', codename: `${scope}.${action}` });
                }
            });
        });
        sendBatch(actions, leftOut);
    };

    const setAllTags = (value, scopeFilter) => {
        const actions = [];
        const leftOut = [];
        Object.keys(existingPerms).forEach((scope) => {
            if (scopeFilter && scope !== scopeFilter) return;
            Object.keys(existingPerms[scope]).forEach((action) => {
                const p = existingPerms[scope][action];
                if (p.has_target_tags && p.all_tags !== value) {
                    if (!value || mayGrantAllTags(scope, action)) actions.push({ op: 'set_all_tags', scope, action, value });
                    else leftOut.push({ op: 'set_all_tags', codename: `${scope}.${action}` });
                }
            });
        });
        sendBatch(actions, leftOut);
    };

    const resetPermissions = () => {
        if (!window.confirm('Reset all permissions to the state when the access group was loaded?')) return;
        const orig = props.originalPermissions || [];
        const origSet = new Set(orig.map((p) => p.perm.codename));
        const currSet = new Set((props.data?.permissions || []).map((p) => p.perm.codename));
        const actions = [];
        // Add back removed permissions
        orig.forEach((p) => {
            const c = p.perm.codename.split('.');
            if (c.length !== 2) return;
            if (!currSet.has(p.perm.codename)) {
                actions.push({ op: 'add', scope: c[0], action: c[1], all_tags: p.all_tags });
            }
        });
        // Remove added permissions
        (props.data?.permissions || []).forEach((p) => {
            const c = p.perm.codename.split('.');
            if (c.length !== 2) return;
            if (!origSet.has(p.perm.codename)) {
                actions.push({ op: 'remove', scope: c[0], action: c[1] });
            }
        });
        // Fix all_tags mismatches
        orig.forEach((p) => {
            const c = p.perm.codename.split('.');
            if (c.length !== 2) return;
            const curr = (props.data?.permissions || []).find((cp) => cp.perm.codename === p.perm.codename);
            if (curr && curr.all_tags !== p.all_tags) {
                actions.push({ op: 'set_all_tags', scope: c[0], action: c[1], value: p.all_tags });
            }
        });
        if (actions.length > 0) props.onBatchPermissions(actions);
    };

    const revokeAllPerms = () => {
        if (!window.confirm('Revoke ALL granted permissions? This cannot be undone.')) return;
        const actions = [];
        Object.keys(existingPerms).forEach((scope) => {
            Object.keys(existingPerms[scope]).forEach((action) => {
                actions.push({ op: 'remove', scope, action });
            });
        });
        if (actions.length > 0) props.onBatchPermissions(actions);
    };

    props.data?.permissions?.forEach((aps) => {
        const c = aps.perm.codename.split('.');
        if (c.length !== 2) return;
        const perm = {
            scope: c[0],
            action: c[1],
            has_target_agroups: aps.perm.has_target_agroups,
            has_target_tags: aps.perm.has_target_tags,
            target_agroups: aps.perm.has_target_agroups ? aps.target_agroups : null,
            id: aps.id,
            atags: aps.atags,
            all_tags: aps.all_tags,
            open: open.includes(c[0]),
        };
        if (!existingPerms[c[0]]) {
            existingPerms[c[0]] = {};
        }
        existingPerms[c[0]][c[1]] = perm;
        if (!exKeys[c[0]]) {
            exKeys[c[0]] = [];
        }
        exKeys[c[0]].push(perm.id);
    });
    Object.keys(exKeys).forEach((x) => (exKeys[x] = exKeys[x].join(',')));
    Object.keys(props.permissionMap).forEach((scope) => {
        const s = props.permissionMap[scope];
        Object.keys(s).forEach((action) => {
            const perm = props.permissionMap[scope][action];
            if (!existingPerms[scope] || !existingPerms[scope][action]) {
                if (!potentialPerms[scope]) {
                    potentialPerms[scope] = {};
                }
                potentialPerms[scope][action] = perm;
            }
        });
    });

    const toggleOpen = (scope) => {
        setOpen((currentOpen) =>
            currentOpen.includes(scope) ? currentOpen.filter((s) => s !== scope) : [...currentOpen, scope]
        );
    };

    const toolbar = hasAccessGroupEditPerm ? (
        <Box display="flex" flexWrap="wrap" gap={1} p={2} pb={1} sx={{ borderBottom: 'solid 1px #D8D8D8' }}>
            <Tooltip title="Grant all available permissions (an AI-over-MCP permission only if you hold it explicitly yourself)">
                <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => grantAllPerms()}>
                    Grant All
                </Button>
            </Tooltip>
            <Tooltip title="Revoke all granted permissions">
                <Button size="small" variant="outlined" color="error" startIcon={<RemoveCircleOutlineIcon />} onClick={revokeAllPerms}>
                    Revoke All
                </Button>
            </Tooltip>
            <Tooltip title={allTagsRestricted ? ALL_TAGS_RESTRICTED_HINT : "Set 'All' switch ON for all permissions"}>
                <span>
                    <Button size="small" variant="outlined" startIcon={<ToggleOnIcon />} onClick={() => setAllTags(true)} disabled={allTagsRestricted}>
                        All Tags On
                    </Button>
                </span>
            </Tooltip>
            <Tooltip title="Set 'All' switch OFF for all permissions">
                <Button size="small" variant="outlined" startIcon={<ToggleOffIcon />} onClick={() => setAllTags(false)}>
                    All Tags Off
                </Button>
            </Tooltip>
            <Tooltip title="Reset permissions to the state when the access group was loaded">
                <Button size="small" variant="outlined" color="warning" startIcon={<RestoreIcon />} onClick={resetPermissions}>
                    Reset
                </Button>
            </Tooltip>
        </Box>
    ) : null;

    const body = Object.keys(props.permissionMap)
        .filter((x) => !SCOPE(x)?.legacy)
        .sort()
        .map((scope, scope_idx) => (
            <Grid item xs={12} pt={2} key={exKeys[scope] || scope} container direction="row" spacing={2} alignItems="center" alignContent="center" style={{ borderBottom:  'solid 1px #D8D8D8'   }} >
                <Grid item xs={12} p={2} container alignItems="center" alignContent="center">
                    <Grid item xs={'auto'} style={{ width: '50px', }}>
                        <Tooltip title={open.includes(scope) ? 'Collapse' : 'Expand'}>
                            <IconButton color="primary" onClick={() => toggleOpen(scope)} >
                                {open.includes(scope) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item xs={'auto'}>
                        <Typography variant="h6">{SCOPE(scope)?.name || capitalize(scope)}</Typography>
                    </Grid>
                    {!open.includes(scope) && (() => {
                        const allActions = orderActions(scope, Object.keys(props.permissionMap[scope] || {}));
                        return (
                            <Grid item xs container alignItems="center" gap={0.5} pl={1} sx={{ flexWrap: 'wrap' }}>
                                {allActions.map((action) => {
                                    const granted = !!existingPerms[scope]?.[action];
                                    return (
                                        <Typography
                                            key={action}
                                            component="span"
                                            sx={{
                                                fontSize: '0.7rem',
                                                px: 0.7,
                                                py: 0.1,
                                                borderRadius: '4px',
                                                backgroundColor: granted ? '#e8f5e9' : '#ffebee',
                                                color: granted ? '#2e7d32' : '#c62828',
                                                lineHeight: 1.6,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {actionName(scope, action) || action}
                                        </Typography>
                                    );
                                })}
                            </Grid>
                        );
                    })()}
                    {hasAccessGroupEditPerm && (
                        <Grid item xs={'auto'} container justifyContent="flex-end" gap={0.5}>
                            {potentialPerms[scope] && Object.keys(potentialPerms[scope]).some((action) => mayGrant(scope, action)) && (
                                <Tooltip title={`Grant all ${SCOPE(scope)?.name || scope} permissions`}>
                                    <Button size="small" variant="text" onClick={() => grantAllPerms(scope)}>Grant All</Button>
                                </Tooltip>
                            )}
                            {existingPerms[scope] && (
                                <>
                                    <Tooltip title={allTagsRestricted ? ALL_TAGS_RESTRICTED_HINT : "Set 'All' ON for this scope"}>
                                        <span>
                                            <Button size="small" variant="text" onClick={() => setAllTags(true, scope)} disabled={allTagsRestricted}>All On</Button>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Set 'All' OFF for this scope">
                                        <Button size="small" variant="text" onClick={() => setAllTags(false, scope)}>All Off</Button>
                                    </Tooltip>
                                    <Tooltip title={`Revoke all ${SCOPE(scope)?.name || scope} permissions`}>
                                        <Button size="small" variant="text" color="error" onClick={() => {
                                            const actions = Object.keys(existingPerms[scope]).map((action) => ({ op: 'remove', scope, action }));
                                            if (actions.length > 0) props.onBatchPermissions(actions);
                                        }}>Revoke All</Button>
                                    </Tooltip>
                                </>
                            )}
                        </Grid>
                    )}
                </Grid>
                {
                    open.includes(scope) && (
                        <>
                            {existingPerms[scope] && (
                                <Grid item xs={12} container alignItems="center" spacing={isSmallScreen ? 0 : 2} pb={2}>
                                    <PermissionScope
                                        open={open.includes(scope)}
                                        isSmallScreen={isSmallScreen}
                                        readOnly={props.readOnly}
                                        scope={scope}
                                        ag={props.data} 
                                        existingPerms={existingPerms[scope]} 
                                        potentialPerms={potentialPerms[scope]} 
                                        onRemovePermission={props.onRemovePermission} 
                                        onAccessTagsChange={props.onAccessTagsChange} 
                                        onTargetAccessGroupsChange={props.onTargetAccessGroupsChange}
                                        onAllTagsSwitch={props.onAllTagsSwitch}
                                        grantOf={grantOf}
                                    />
                                </Grid>
                            )}
                            {potentialPerms[scope] && (
                                <Grid item xs={12} pb={2} container alignItems="center" justifyContent="flex-end">
                                    <Grid item xs={12} container alignItems="center" justifyContent={"flex-start"} p={isSmallScreen ? 1 : 0}>
                                        <Typography fontWeight={500} pl={isSmallScreen ? 0 : 2}>Add {SCOPE(scope)?.name || capitalize(scope)} permissions</Typography>
                                    </Grid>
                                        {orderActions(scope, Object.keys(potentialPerms[scope]))
                                            .map((action, idx, toAdd) => (
                                            <React.Fragment key={action}>
                                            {startsMcpGroup(scope, toAdd, idx) && <McpGroupHeading isSmallScreen={isSmallScreen} />}
                                            <Grid item xs={12} p={1}pl={isSmallScreen ? 1 : 2}>
                                                {/* Q9: an MCP permission the granter certainly does not hold is not theirs to grant */}
                                                <Tooltip title={grantOf(scope, action)?.held === NO ? explicitHint(scope, action) : ''}>
                                                    <span>
                                                    <Button fullWidth sx={{ border:'solid 1px #D8D8D8',maxWidth:isSmallScreen ? '100%' : '250px' }} variant="outlined" disabled={!hasAccessGroupEditPerm || grantOf(scope, action)?.held === NO} onClick={() => props.onAddPermission(scope, action)}>
                                                        {actionName(scope, action) || action}
                                                    </Button>
                                                    </span>
                                                </Tooltip>
                                            </Grid>
                                            </React.Fragment>
                                            ))}
                                </Grid>
                            )}
                        </>
                    )
                }
            </Grid >
        ));
    return <>{toolbar}{body}</>;
};

export default AccessGroupEditPermissions;
